import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { BackerPopup } from '@components/BackerPopup';
import { EarliestCommentCard } from '@components/CommentCard/EarliestCommentCard';
import { useAudience } from '@components/Song/Audience/Earliest/useAudience';
import { LoadMore } from '@components/Song/Audience/LoadMore';
import { getImageSize, useAudienceBackersWithEmpties } from '@components/Song/Audience/helpers';
import { getRow } from '@components/Song/Audience/helpers';
import { SeatingSkeleton } from '@components/Song/Audience/shared/SeatingSkeleton';
import {
  DEFAULT_MOBILE_SEAT_COUNT,
  DEFAULT_TABLET_SEAT_COUNT,
  MOBILE_MAX_SEATS,
  SECOND_THIRD_ROW_DESKTOP,
  SECOND_THIRD_ROW_MOBILE,
  TABLET_MAX_SEATS,
} from '@components/Song/Audience/shared/constants';
import { GoldenEggContainer, Seat, SeatingGrid } from '@components/Song/Audience/shared/styles';
import { GoldenEgg } from '@components/basics/GoldenEgg';
import { Avatar } from '@components/basics/Images/Avatar';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { AudienceSeatingSongInfoFragmentDoc, getFragment } from '@graphql/reactQuery';
import { useAudienceSeatCount } from '@hooks/useAudienceSeatCount';
import type { CommentType } from '../../../../types/comments';
import { parseCommentType } from '../../../../types/comments';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment AudienceSeatingSongInfo on Release {
    id
    ...AudienceSeatCount
    ...ReleaseCommentType
  }
`);

/**
 * @dev When updating this component make sure to double check the expected states from the link below
 * https://www.figma.com/file/CfLYoS23bdpkE1z0X8cQXL/Song-Page-2.0?node-id=2839%3A238844&t=3UokwbmEJG9tNBnA-1
 */
// TO DO : https://linear.app/sound/issue/ENG-4075/[ui]-implement-virtualized-list-on-earliest-audience-section
export function AudienceSeating({
  releaseFrag,
}: {
  releaseFrag: FragmentType<AudienceSeatingSongInfoFragmentDoc>;
}) {
  const { tablet, tabletLandscape, laptop } = MediaQuery.useContainer();

  const release = getFragment(AudienceSeatingSongInfoFragmentDoc, releaseFrag) || null;
  const releaseId = release?.id;
  const { nfts, loading, hasNextPage, fetchMore } = useAudience({
    releaseId,
    sort: {
      serialNumber: 'ASC',
    },
    paginationCount: tablet ? TABLET_MAX_SEATS : MOBILE_MAX_SEATS,
  });

  const backersData = React.useMemo(() => {
    return nfts && nfts.length > 0
      ? nfts?.map(nft => {
          return parseCommentType({
            nft,
            release,
          });
        })
      : undefined;
  }, [nfts, release]);

  const displayCondition = backersData ? backersData.length > 0 && nfts != null : false;
  const paginateMoreCondition =
    backersData && backersData.length > 0 && displayCondition && hasNextPage;

  const showLoadMore = paginateMoreCondition || loading;

  const {
    numberOfSeats,
    quantityUpperBound,
    quantityLowerBound,
    isBeforeFinalSaleEndTime,
    remaining,
  } = useAudienceSeatCount({
    release,
  });

  const emptySeatCount = tablet ? DEFAULT_TABLET_SEAT_COUNT : DEFAULT_MOBILE_SEAT_COUNT;
  const maxSeats = tablet ? TABLET_MAX_SEATS : MOBILE_MAX_SEATS;

  const backersWithEmpties = useAudienceBackersWithEmpties({
    numberOfSeats,
    backersData,
    quantityUpperBound,
    quantityLowerBound,
    // Airdrops can sell the full drop out before the final sale end time
    isPrimarySaleActive: remaining === 0 ? false : isBeforeFinalSaleEndTime,
    maxSeats,
  });

  const backerProfiles = React.useMemo(() => {
    const backers = !backersData
      ? Array.from<CommentType | undefined>({ length: emptySeatCount })
      : backersWithEmpties;

    const midRow = tablet ? SECOND_THIRD_ROW_DESKTOP : SECOND_THIRD_ROW_MOBILE;

    return backers.map((backer, i) => {
      const row = getRow({
        index: i,
        midRowMax: midRow,
      });
      if (!backer) {
        return <Seat className={row} key={`empty-seat-${i}`} empty />;
      }

      return (
        <BackerPopup
          key={`backer-popup-${i}`}
          content={<EarliestCommentCard comment={backer} audience />}
        >
          <Seat className={row}>
            {backer.isEggWinner && (
              <GoldenEggContainer row={row}>
                <GoldenEgg />
              </GoldenEggContainer>
            )}
            <Avatar
              size={getImageSize({
                desktopBreakpoint: laptop,
                rowPos: row,
              })}
              disableLink={!tabletLandscape}
              src={backer.avatarUrl}
              username={backer.username}
              walletAddress={backer.publicAddress}
              borderRadius={8}
              priority={false}
              webappUri={backer.webappUri}
            />
          </Seat>
        </BackerPopup>
      );
    });
  }, [backersData, emptySeatCount, backersWithEmpties, tablet, laptop, tabletLandscape]);

  const [showRest, setShowRest] = React.useState(false);

  if (loading && nfts.length === 0) {
    return <SeatingSkeleton />;
  }

  return (
    <>
      <SeatingGrid>{backerProfiles}</SeatingGrid>
      {showLoadMore ? (
        <LoadMore
          onClick={() => {
            if (!showRest) setShowRest(true);
            fetchMore();
          }}
          loading={loading}
          seating
        />
      ) : (
        backersData &&
        backersWithEmpties.length > maxSeats &&
        !showRest && <LoadMore onClick={() => setShowRest(prev => !prev)} loading={false} seating />
      )}
    </>
  );
}
